import { lazy, type ComponentType } from 'react';

/* ===========================================================================
 * EVERY PAGE, LAZILY — the registry, out of the route table.
 *
 * `App.tsx` is two things bolted together: a list of 57 dynamic imports, and
 * the route table that says who may reach what. Only the second is worth
 * reading, and the first was two hundred lines in front of it.
 *
 * Split on the file-size gate, and the seam is safe: several tests read
 * `App.tsx` as SOURCE to prove things about the table — that every role-gated
 * nav destination has a gated route (`routeGates`), that no route's element is
 * only a redirect (`navigationWiring`), that every camp route carries its
 * guard (`campsPillar`). All of them parse `<Route>` elements, none of them
 * parse these declarations, and the table stays where it is.
 * ======================================================================== */

// Lazily load page components so each route ships in its own chunk. Initial
// bundle drops dramatically and heavy deps (leaflet, recharts, react-leaflet)
// only load on the routes that need them.
function lazyNamed<K extends string>(
  loader: () => Promise<Record<K, ComponentType<Record<string, never>>>>,
  name: K
) {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[name] };
  });
}

export const LoginPage = lazyNamed(() => import('./routes/auth/LoginPage.js'), 'LoginPage');
export const ForgotPasswordPage = lazyNamed(
  () => import('./routes/auth/ForgotPasswordPage.js'),
  'ForgotPasswordPage'
);
export const ResetPasswordPage = lazyNamed(
  () => import('./routes/auth/ResetPasswordPage.js'),
  'ResetPasswordPage'
);
export const ChangePasswordPage = lazyNamed(
  () => import('./routes/auth/ChangePasswordPage.js'),
  'ChangePasswordPage'
);
export const PersonalDashboardPage = lazyNamed(
  () => import('./routes/dashboard/DashboardPage.js'),
  'PersonalDashboardPage'
);
export const AdminDashboardPage = lazyNamed(
  () => import('./routes/dashboard/DashboardPage.js'),
  'AdminDashboardPage'
);
export const CombinedDashboardPage = lazyNamed(
  () => import('./routes/dashboard/DashboardPage.js'),
  'CombinedDashboardPage'
);
export const EmployeeListPage = lazyNamed(
  () => import('./routes/employees/EmployeeListPage.js'),
  'EmployeeListPage'
);
export const EmployeeDetailPage = lazyNamed(
  () => import('./routes/employees/EmployeeDetailPage.js'),
  'EmployeeDetailPage'
);
export const WorkerAttendanceHistoryPage = lazyNamed(
  () => import('./routes/employees/WorkerAttendanceHistoryPage.js'),
  'WorkerAttendanceHistoryPage'
);
export const EmployeeFormPage = lazyNamed(
  () => import('./routes/employees/EmployeeFormPage.js'),
  'EmployeeFormPage'
);
export const BulkImportPage = lazyNamed(
  () => import('./routes/employees/BulkImportPage.js'),
  'BulkImportPage'
);
export const AnnouncementsPage = lazyNamed(
  () => import('./routes/announcements/AnnouncementsPage.js'),
  'AnnouncementsPage'
);
export const LettersPage = lazyNamed(() => import('./routes/letters/LettersPage.js'), 'LettersPage');
export const ErpApprovalsPage = lazyNamed(
  () => import('./routes/erp/ErpApprovalsPage.js'),
  'ErpApprovalsPage'
);
export const DepartmentsPage = lazyNamed(
  () => import('./routes/org/DepartmentsPage.js'),
  'DepartmentsPage'
);
export const DesignationsPage = lazyNamed(
  () => import('./routes/org/DesignationsPage.js'),
  'DesignationsPage'
);
export const BranchesPage = lazyNamed(() => import('./routes/org/BranchesPage.js'), 'BranchesPage');
export const LaborSuppliersPage = lazyNamed(
  () => import('./routes/org/LaborSuppliersPage.js'),
  'LaborSuppliersPage'
);
export const ProfilePage = lazyNamed(() => import('./routes/profile/ProfilePage.js'), 'ProfilePage');
export const WorksiteListPage = lazyNamed(
  () => import('./routes/worksites/WorksiteListPage.js'),
  'WorksiteListPage'
);
export const WorksiteFormPage = lazyNamed(
  () => import('./routes/worksites/WorksiteFormPage.js'),
  'WorksiteFormPage'
);
export const SiteAttendanceHistoryPage = lazyNamed(
  () => import('./routes/worksites/SiteAttendanceHistoryPage.js'),
  'SiteAttendanceHistoryPage'
);
export const WorksiteDetailPage = lazyNamed(
  () => import('./routes/worksites/WorksiteDetailPage.js'),
  'WorksiteDetailPage'
);
export const CampOccupancyPage = lazyNamed(
  () => import('./routes/worksites/CampOccupancyPage.js'),
  'CampOccupancyPage'
);
export const OperationsPage = lazyNamed(
  () => import('./routes/operations/OperationsPage.js'),
  'OperationsPage'
);
export const MyAttendancePage = lazyNamed(
  () => import('./routes/attendance/MyAttendancePage.js'),
  'MyAttendancePage'
);
export const CorrectionsPage = lazyNamed(
  () => import('./routes/attendance/CorrectionsPage.js'),
  'CorrectionsPage'
);
export const AttendanceCalendarPage = lazyNamed(
  () => import('./routes/attendance/AttendanceCalendarPage.js'),
  'AttendanceCalendarPage'
);
export const AttendanceDayPage = lazyNamed(
  () => import('./routes/attendance/AttendanceDayPage.js'),
  'AttendanceDayPage'
);
export const ShiftsPage = lazyNamed(() => import('./routes/shifts/ShiftsPage.js'), 'ShiftsPage');
export const SchedulePage = lazyNamed(() => import('./routes/shifts/SchedulePage.js'), 'SchedulePage');
export const EmployeeLifecyclePage = lazyNamed(
  () => import('./routes/lifecycle/EmployeeLifecyclePage.js'),
  'EmployeeLifecyclePage'
);
export const MyLeavePage = lazyNamed(() => import('./routes/leave/MyLeavePage.js'), 'MyLeavePage');
export const LeaveManagePage = lazyNamed(
  () => import('./routes/leave/LeaveManagePage.js'),
  'LeaveManagePage'
);
export const LeaveTypesPage = lazyNamed(
  () => import('./routes/leave/LeaveTypesPage.js'),
  'LeaveTypesPage'
);
export const HolidaysPage = lazyNamed(() => import('./routes/leave/HolidaysPage.js'), 'HolidaysPage');
export const PayrollPrepPage = lazyNamed(
  () => import('./routes/payroll/PayrollPrepPage.js'),
  'PayrollPrepPage'
);
export const TeamDirectoryPage = lazyNamed(
  () => import('./routes/team/TeamDirectoryPage.js'),
  'TeamDirectoryPage'
);
export const TeamAttendancePage = lazyNamed(
  () => import('./routes/team/TeamAttendancePage.js'),
  'TeamAttendancePage'
);
export const ReportsLayout = lazyNamed(
  () => import('./routes/reports/ReportsLayout.js'),
  'ReportsLayout'
);
export const ReportsHomePage = lazyNamed(
  () => import('./routes/reports/ReportsHomePage.js'),
  'ReportsHomePage'
);
export const ReportsWorkspace = lazyNamed(
  () => import('./routes/reports/workspace/ReportsWorkspace.js'),
  'ReportsWorkspace'
);
export const SmartReportPage = lazyNamed(
  () => import('./routes/reports/smart/SmartReportPage.js'),
  'SmartReportPage'
);
export const TimesheetReport = lazyNamed(
  () => import('./routes/reports/TimesheetReport.js'),
  'TimesheetReport'
);
export const SiteCampManpowerReport = lazyNamed(
  () => import('./routes/reports/SiteCampManpowerReport.js'),
  'SiteCampManpowerReport'
);
export const ExecDashboard = lazyNamed(
  () => import('./routes/dashboard/ExecDashboard.js'),
  'ExecDashboard'
);
export const LeaveBalanceReport = lazyNamed(
  () => import('./routes/reports/LeaveBalanceReport.js'),
  'LeaveBalanceReport'
);
export const LabourCostReport = lazyNamed(
  () => import('./routes/reports/LabourCostReport.js'),
  'LabourCostReport'
);
export const GratuityCalculatorPage = lazyNamed(
  () => import('./routes/reports/GratuityCalculatorPage.js'),
  'GratuityCalculatorPage'
);
export const WpsExportPage = lazyNamed(
  () => import('./routes/reports/WpsExportPage.js'),
  'WpsExportPage'
);
export const AuditLogPage = lazyNamed(
  () => import('./routes/system/AuditLogPage.js'),
  'AuditLogPage'
);
export const JobsPage = lazyNamed(() => import('./routes/system/JobsPage.js'), 'JobsPage');
export const SettingsPage = lazyNamed(
  () => import('./routes/settings/SettingsPage.js'),
  'SettingsPage'
);
export const DevicesPage = lazyNamed(() => import('./routes/settings/DevicesPage.js'), 'DevicesPage');
export const NotificationsPage = lazyNamed(
  () => import('./routes/notifications/NotificationsPage.js'),
  'NotificationsPage'
);
export const NotFoundPage = lazyNamed(() => import('./routes/NotFoundPage.js'), 'NotFoundPage');
